// Bench Candidates (ADR-107) — enum mirrors of radar-api's BenchCandidate* Prisma
// enums, plus labels / chip colors / option lists for the report page.

export const BENCH_CANDIDATE_SOURCE_SYSTEMS = {
  AMERICAS: 'AMERICAS',
};

export const BENCH_CANDIDATE_SOURCE_SYSTEM_OPTIONS = [
  { value: BENCH_CANDIDATE_SOURCE_SYSTEMS.AMERICAS, label: 'Americas' },
];

/** Display name of a source system ("Americas"), for copy that names the feed. */
export const benchCandidateSourceSystemLabel = (sourceSystem) => (
  BENCH_CANDIDATE_SOURCE_SYSTEM_OPTIONS.find((opt) => opt.value === sourceSystem)?.label || sourceSystem || 'the source system'
);

// Fine-grained upstream reason (three real values + the UNKNOWN sentinel).
export const BENCH_CANDIDATE_SOURCES = {
  ROLLING_OFF_STAFF_AUG: 'ROLLING_OFF_STAFF_AUG',
  ROLLING_OFF_PROJECT: 'ROLLING_OFF_PROJECT',
  RECENTLY_LEFT_ELIGIBLE_TALENT: 'RECENTLY_LEFT_ELIGIBLE_TALENT',
  UNKNOWN: 'UNKNOWN',
};

export const BENCH_CANDIDATE_SOURCE_LABELS = {
  [BENCH_CANDIDATE_SOURCES.ROLLING_OFF_STAFF_AUG]: 'Rolling off – Staff Aug',
  [BENCH_CANDIDATE_SOURCES.ROLLING_OFF_PROJECT]: 'Rolling off – Project',
  [BENCH_CANDIDATE_SOURCES.RECENTLY_LEFT_ELIGIBLE_TALENT]: 'Recently left – Eligible Talent',
  [BENCH_CANDIDATE_SOURCES.UNKNOWN]: 'Unknown reason',
};

export const BENCH_CANDIDATE_SOURCE_COLORS = {
  [BENCH_CANDIDATE_SOURCES.ROLLING_OFF_STAFF_AUG]: 'info',
  [BENCH_CANDIDATE_SOURCES.ROLLING_OFF_PROJECT]: 'info',
  [BENCH_CANDIDATE_SOURCES.RECENTLY_LEFT_ELIGIBLE_TALENT]: 'default',
  [BENCH_CANDIDATE_SOURCES.UNKNOWN]: 'warning',
};

export const BENCH_CANDIDATE_SOURCE_OPTIONS = Object.values(BENCH_CANDIDATE_SOURCES).map((value) => ({
  value,
  label: BENCH_CANDIDATE_SOURCE_LABELS[value],
}));

export const BENCH_CANDIDATE_CATEGORIES = {
  ROLLING_OFF: 'ROLLING_OFF',
  RECENTLY_LEFT: 'RECENTLY_LEFT',
  UNKNOWN: 'UNKNOWN',
};

export const BENCH_CANDIDATE_MATCH_STATUSES = {
  MATCHED: 'MATCHED',
  NOT_IN_RADAR: 'NOT_IN_RADAR',
  AMBIGUOUS: 'AMBIGUOUS',
};

export const BENCH_CANDIDATE_MATCH_STATUS_LABELS = {
  [BENCH_CANDIDATE_MATCH_STATUSES.MATCHED]: 'In MySM',
  [BENCH_CANDIDATE_MATCH_STATUSES.NOT_IN_RADAR]: 'Not in MySM',
  [BENCH_CANDIDATE_MATCH_STATUSES.AMBIGUOUS]: 'Ambiguous match',
};

export const BENCH_CANDIDATE_MATCH_STATUS_COLORS = {
  [BENCH_CANDIDATE_MATCH_STATUSES.MATCHED]: 'success',
  [BENCH_CANDIDATE_MATCH_STATUSES.NOT_IN_RADAR]: 'warning',
  [BENCH_CANDIDATE_MATCH_STATUSES.AMBIGUOUS]: 'error',
};

export const BENCH_CANDIDATE_MATCH_STATUS_OPTIONS = Object.values(BENCH_CANDIDATE_MATCH_STATUSES).map((value) => ({
  value,
  label: BENCH_CANDIDATE_MATCH_STATUS_LABELS[value],
}));

// Human-owned work-list state.
export const BENCH_CANDIDATE_STATUSES = {
  NEW: 'NEW',
  ASSIGNED: 'ASSIGNED',
  DISMISSED: 'DISMISSED',
  REMOVED_UPSTREAM: 'REMOVED_UPSTREAM',
};

export const BENCH_CANDIDATE_STATUS_LABELS = {
  [BENCH_CANDIDATE_STATUSES.NEW]: 'New',
  [BENCH_CANDIDATE_STATUSES.ASSIGNED]: 'Assigned to bench',
  [BENCH_CANDIDATE_STATUSES.DISMISSED]: 'Dismissed',
  [BENCH_CANDIDATE_STATUSES.REMOVED_UPSTREAM]: 'No longer a candidate',
};

export const BENCH_CANDIDATE_STATUS_COLORS = {
  [BENCH_CANDIDATE_STATUSES.NEW]: 'info',
  [BENCH_CANDIDATE_STATUSES.ASSIGNED]: 'success',
  [BENCH_CANDIDATE_STATUSES.DISMISSED]: 'default',
  [BENCH_CANDIDATE_STATUSES.REMOVED_UPSTREAM]: 'default',
};

export const BENCH_CANDIDATE_STATUS_OPTIONS = Object.values(BENCH_CANDIDATE_STATUSES).map((value) => ({
  value,
  label: BENCH_CANDIDATE_STATUS_LABELS[value],
}));

// Eligibility reason codes (radar-api config/benchCandidateEligibility.js).
export const BENCH_CANDIDATE_ELIGIBILITY_LABELS = {
  REMOVED_UPSTREAM: 'No longer listed in the source feed', // reasonLine() names the feed ("in the Americas feed")
  LEFT_ORGANISATION: 'Has left the organisation (recently left, inactive, or consultant contract ended)',
  NOT_IN_RADAR: 'Not in MySM — import first',
  NO_OPEN_EPISODE: 'No open employment in MySM',
  ALREADY_ON_BENCH: 'Already on a bench project',
  ALREADY_ASSIGNED: 'Already assigned from this list',
  HAS_FUTURE_SOW: 'Has a future SOW',
  UNKNOWN_BENCH_SOURCE: 'Unmapped reason in the source feed',
  DISMISSED: 'Dismissed',
  ROLL_OFF_FAR: 'Roll-off is far out — the bench row will be future-dated',
};

// SyncRunStatusEnum (reused).
export const BENCH_CANDIDATE_RUN_STATUSES = {
  RUNNING: 'running',
  SUCCESS: 'success',
  FAILED: 'failed',
};

export const BENCH_CANDIDATE_RUN_STATUS_LABELS = {
  [BENCH_CANDIDATE_RUN_STATUSES.RUNNING]: 'Running',
  [BENCH_CANDIDATE_RUN_STATUSES.SUCCESS]: 'Success',
  [BENCH_CANDIDATE_RUN_STATUSES.FAILED]: 'Failed',
};

export const BENCH_CANDIDATE_RUN_STATUS_COLORS = {
  [BENCH_CANDIDATE_RUN_STATUSES.RUNNING]: 'info',
  [BENCH_CANDIDATE_RUN_STATUSES.SUCCESS]: 'success',
  [BENCH_CANDIDATE_RUN_STATUSES.FAILED]: 'error',
};

// defaultDirection: the direction applied on first click (a second click flips it).
export const BENCH_CANDIDATE_SORT_OPTIONS = [
  { value: 'rollOffDate', label: 'Roll-off date', defaultDirection: 'asc' },        // soonest roll-off first
  { value: 'firstSeenAt', label: 'First seen', defaultDirection: 'desc' },           // newest arrivals first
  { value: 'fullName', label: 'Individual', defaultDirection: 'asc' },
];
